import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../services/api';

const PRESETS = [
  { label: '☕ $5',  cents: 500,  note: 'A latte'       },
  { label: '🍕 $10', cents: 1000, note: 'A slice'       },
  { label: '🍺 $20', cents: 2000, note: 'A round'       },
];

export default function BuyMeALatte() {
  const location = useLocation();
  const [selected,    setSelected]    = useState(500);   // default $5
  const [customRaw,   setCustomRaw]   = useState('');    // raw input string
  const [useCustom,   setUseCustom]   = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [justDonated, setJustDonated] = useState(false);

  // Show a thank-you if Stripe redirected back with ?donated=1
  useEffect(() => {
    if (new URLSearchParams(location.search).get('donated') === '1') {
      setJustDonated(true);
    }
  }, [location.search]);

  const activeCents = useCustom
    ? Math.round(parseFloat(customRaw || '0') * 100)
    : selected;

  const isValid = activeCents >= 100 && activeCents <= 50000;

  const handleDonate = async () => {
    if (!isValid) {
      setError('Please enter an amount between $1 and $500.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { url } = await api.createDonationCheckout(activeCents);
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (justDonated) {
    return (
      <div className="card" style={{ marginBottom: '1rem', textAlign: 'center', padding: '1.5rem' }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>☕</div>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18,
                      color: '#0d2b1a', marginBottom: 6 }}>
          Thank you so much!
        </div>
        <div style={{ fontSize: 13, color: '#7a7a6a', lineHeight: 1.6 }}>
          Your support means a lot and helps keep Greentime free for everyone.
          Now go enjoy that round. 🏌️
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.75rem' }}>
        <div style={{ fontSize: 28 }}>☕</div>
        <div>
          <div style={{ fontWeight: 500, fontSize: 14, color: '#0d2b1a' }}>
            Buy me a latte
          </div>
          <div style={{ fontSize: 12, color: '#7a7a6a', marginTop: 1 }}>
            Greentime is free. If you're enjoying it, a small contribution
            helps cover hosting and keeps new features coming.
          </div>
        </div>
      </div>

      {/* Preset amounts */}
      <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        {PRESETS.map(p => (
          <button
            key={p.cents}
            onClick={() => { setSelected(p.cents); setUseCustom(false); setError(''); }}
            style={{
              flex: 1, minWidth: 70,
              padding: '8px 6px',
              borderRadius: 8,
              border: `1.5px solid ${!useCustom && selected === p.cents ? '#0d2b1a' : '#ddd8c8'}`,
              background: !useCustom && selected === p.cents ? '#0d2b1a' : '#faf8f4',
              color: !useCustom && selected === p.cents ? '#7ec99a' : '#3a3a30',
              cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 500,
              textAlign: 'center',
              transition: 'all 0.15s',
            }}
          >
            <div>{p.label}</div>
            <div style={{ fontSize: 10, opacity: 0.65, marginTop: 1 }}>{p.note}</div>
          </button>
        ))}

        {/* Custom amount button */}
        <button
          onClick={() => { setUseCustom(true); setError(''); }}
          style={{
            flex: 1, minWidth: 70,
            padding: '8px 6px',
            borderRadius: 8,
            border: `1.5px solid ${useCustom ? '#0d2b1a' : '#ddd8c8'}`,
            background: useCustom ? '#0d2b1a' : '#faf8f4',
            color: useCustom ? '#7ec99a' : '#3a3a30',
            cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13,
            fontWeight: 500,
            textAlign: 'center',
            transition: 'all 0.15s',
          }}
        >
          <div>✏️ Other</div>
          <div style={{ fontSize: 10, opacity: 0.65, marginTop: 1 }}>Your choice</div>
        </button>
      </div>

      {/* Custom amount input */}
      {useCustom && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
              fontSize: 14, color: '#5a5a4a', pointerEvents: 'none',
            }}>$</span>
            <input
              className="form-input"
              type="number"
              min="1"
              max="500"
              step="1"
              value={customRaw}
              onChange={e => { setCustomRaw(e.target.value); setError(''); }}
              placeholder="Enter amount"
              autoFocus
              style={{ paddingLeft: 24 }}
            />
          </div>
          {customRaw && !isNaN(parseFloat(customRaw)) && (
            <div style={{ fontSize: 11, color: '#7a7a6a', marginTop: 4 }}>
              ${parseFloat(customRaw).toFixed(2)} — thank you! 🙏
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="form-error" style={{ marginBottom: '0.65rem' }}>{error}</p>
      )}

      {/* Donate button */}
      <button
        className="btn btn-accent"
        style={{ width: '100%', justifyContent: 'center', gap: 6 }}
        onClick={handleDonate}
        disabled={loading || (useCustom && !isValid)}
      >
        {loading
          ? 'Redirecting to checkout…'
          : `☕ Contribute ${isValid ? '$' + (activeCents / 100).toFixed(activeCents % 100 === 0 ? 0 : 2) : ''}` }
      </button>

      <div style={{ fontSize: 11, color: '#9a9a8a', textAlign: 'center', marginTop: 8 }}>
        Secure payment via Stripe · No account needed · One-time only
      </div>

    </div>
  );
}
